import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer and keyboard navigation.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  /** @type {HTMLElement | null} Element to return focus to when the drawer closes. */
  #opener = null;

  /** @type {number | null} Pending rAF that registers the outside-click listener after open(). */
  #outsideClickRaf = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
    if (this.#outsideClickRaf != null) {
      cancelAnimationFrame(this.#outsideClickRaf);
      this.#outsideClickRaf = null;
    }
    document.removeEventListener('click', this.#handleDocumentClick);
  }

  /**
   * Handle document clicks to close the header menu when clicking outside
   */
  #handleDocumentClick = (event) => {
    if (this.isOpen && event.target && !this.contains(event.target)) {
      this.close();
    }
  };

  /**
   * Handle keyboard navigation for the entire menu drawer
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    // Handle escape key for backing out of hierarchy levels or closing drawer
    if (event.key === 'Escape') {
      this.#handleEscapeKey(event);
      return;
    }

    // Handle arrow key navigation
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
      this.#handleArrowKeys(event);
      return;
    }
  };

  /**
   * Handle escape key press for backing out of menu hierarchy
   * @param {KeyboardEvent} event
   */
  #handleEscapeKey(event) {
    const currentTarget = event.target;
    if (!currentTarget || !(currentTarget instanceof Element)) return;

    const level = this.#getNavigationLevel(currentTarget);

    switch (level) {
      case 3: // Level 3: collapse accordion, focus on accordion summary
        this.#handleLevel3Escape(event, currentTarget);
        break;
      case 2: // Level 2: focus on level 1 element that opens panel (don't close)
        this.#handleLevel2Escape(event, currentTarget);
        break;
      case 1: // Level 1: focus back to the "Shop" nav open toggle
        this.#handleLevel1Escape(event, currentTarget);
        break;
      default: // Close the entire drawer
        this.#close(this.refs.details);
        break;
    }
  }

  /**
   * Handle arrow key navigation
   * @param {KeyboardEvent} event
   */
  #handleArrowKeys(event) {
    const currentTarget = event.target;
    if (!currentTarget || !(currentTarget instanceof Element)) return;

    // Handle navigation for both summary elements and anchor elements
    if (currentTarget.tagName !== 'SUMMARY' && currentTarget.tagName !== 'A' && currentTarget.tagName !== 'BUTTON') return;

    const level = this.#getNavigationLevel(currentTarget);

    if (event.key === 'ArrowLeft') {
      this.#handleLeftArrow(event, currentTarget, level);
    } else if (event.key === 'ArrowRight') {
      this.#handleRightArrow(event, currentTarget, level);
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      this.#handleVerticalNavigation(event, currentTarget, level);
    }
  }

  /**
   * Determine the navigation level of the current element
   * @param {Element} element
   * @returns {number} 1, 2, or 3
   */
  #getNavigationLevel(element) {
    // Level 3: Inside an open accordion (grandchild list)
    if (element.closest('.menu-drawer__menu--grandchildlist')) {
      return 3;
    }

    // Level 2: Inside submenu or child list
    if (element.closest('.menu-drawer__submenu') || element.closest('.menu-drawer__menu--childlist')) {
      return 2;
    }

    // Level 1: Top level menu
    if (element.closest('.menu-drawer__navigation')) {
      return 1;
    }

    return 0; // Outside navigation area
  }

  /**
   * Handle escape from level 3 - collapse accordion, focus on summary
   * @param {KeyboardEvent} event
   * @param {Element} currentTarget
   */
  #handleLevel3Escape(event, currentTarget) {
    event.preventDefault();
    const parentDetails = currentTarget.closest('details');
    const parentSummary = parentDetails?.querySelector('summary');
    if (parentDetails && parentSummary && 'focus' in parentSummary) {
      parentDetails.removeAttribute('open');
      parentSummary.focus();
    }
  }

  /**
   * Handle escape from level 2 - focus on level 1 element (don't close panel)
   * @param {KeyboardEvent} event
   * @param {Element} currentTarget
   */
  #handleLevel2Escape(event, currentTarget) {
    event.preventDefault();
    // Find the level 1 element that opened this submenu
    const submenu = currentTarget.closest('.menu-drawer__submenu');
    if (submenu) {
      const submenuContainer = submenu.closest('.menu-drawer__menu-container');
      const topLevelSummary = submenuContainer?.querySelector('summary');
      if (topLevelSummary && 'focus' in topLevelSummary) {
        topLevelSummary.focus();
      }
    } else {
      // If in a child list, find the parent top-level item
      const topLevelMenu = this.querySelector('.menu-drawer__menu-top-level');
      const firstNavigableItem = topLevelMenu?.querySelector('summary, a.menu-drawer__menu-item--mainlist');
      if (firstNavigableItem && firstNavigableItem instanceof HTMLElement) {
        firstNavigableItem.focus();
      }
    }
  }

  /**
   * Handle escape from level 1 - close the entire menu
   * @param {KeyboardEvent} event
   * @param {Element} currentTarget
   */
  #handleLevel1Escape(event, currentTarget) {
    // Close the entire drawer (no preventDefault to allow default behavior)
    this.#close(this.refs.details);
  }

  /**
   * Handle left arrow key - go back to level above
   * @param {KeyboardEvent} event
   * @param {Element} currentTarget
   * @param {number} level
   */
  #handleLeftArrow(event, currentTarget, level) {
    event.preventDefault();

    switch (level) {
      case 3:
        this.#handleLevel3Escape(event, currentTarget);
        break;
      case 2:
        this.#handleLevel2Escape(event, currentTarget);
        break;
      case 1:
        this.#handleLevel1Escape(event, currentTarget);
        break;
    }
  }

  /**
   * Handle right arrow key - enter into next level
   * @param {KeyboardEvent} event
   * @param {Element} currentTarget
   * @param {number} level
   */
  #handleRightArrow(event, currentTarget, level) {
    if (level === 1 && currentTarget.tagName === 'SUMMARY') {
      // Delegate to accordions-controller for level 1 to ensure only one accordion is open
      const accordionsController = this.querySelector('accordions-controller');
      if (accordionsController) {
        accordionsController.handleRightArrowNavigation(event, currentTarget);
      }
    } else if (level === 2 && currentTarget.tagName === 'SUMMARY') {
      event.preventDefault();
      // Open accordion and focus first child item
      const details = currentTarget.closest('details');
      if (details) {
        details.setAttribute('open', '');
        requestAnimationFrame(() => details.classList.add('menu-open'));
        onAnimationEnd(details, () => {
          const firstChildItem = details.querySelector('.menu-drawer__menu--grandchildlist a.menu-drawer__menu-item');
          if (firstChildItem && firstChildItem instanceof HTMLElement) {
            firstChildItem.focus();
          }
        });
      }
    }
  }

  /**
   * Handle vertical navigation (up/down arrows)
   * @param {KeyboardEvent} event
   * @param {Element} currentTarget
   * @param {number} level
   */
  #handleVerticalNavigation(event, currentTarget, level) {
    /** @type {Element[]} */
    let allItems = [];

    switch (level) {
      case 1:
        allItems = this.#getLevel1NavigableItems();
        break;
      case 2:
        allItems = this.#getLevel2NavigableItems(currentTarget);
        break;
      case 3:
        allItems = this.#getLevel3NavigableItems(currentTarget);
        break;
    }

    this.#navigateWithinList(event, currentTarget, allItems);
  }

  /**
   * Get all navigable items at level 1
   * @returns {Element[]}
   */
  #getLevel1NavigableItems() {
    const topLevelMenu = this.querySelector('.menu-drawer__navigation');
    if (!topLevelMenu) return [];

    // Use new level-specific selectors for better performance
    return Array.from(topLevelMenu.querySelectorAll('[id^="HeaderDrawer-l1-"], [role="menuitem"]'));
  }

  /**
   * Get all navigable items at level 2
   * @param {Element} currentTarget
   * @returns {Element[]}
   */
  #getLevel2NavigableItems(currentTarget) {
    const submenu = currentTarget.closest('.menu-drawer__inner-submenu');

    if (submenu) {
      // Use level-specific selectors for better performance
      return Array.from(submenu.querySelectorAll('[id^="HeaderDrawer-l2-"], details[open] > .menu-drawer__menu--grandchildlist [id^="HeaderDrawer-l3-"]'));
    }

    return [];
  }

  /**
   * Get all navigable items at level 3
   * @param {Element} currentTarget
   * @returns {Element[]}
   */
  #getLevel3NavigableItems(currentTarget) {
    const grandchildList = currentTarget.closest('.menu-drawer__inner-submenu');
    if (!grandchildList) return [];

    // Use level-specific selectors for better performance
    return Array.from(grandchildList.querySelectorAll('[id^="HeaderDrawer-l2-"], [id^="HeaderDrawer-l3-"], a.menu-drawer__menu-item'));
  }

  /**
   * Navigate within a list of elements
   * @param {KeyboardEvent} event
   * @param {Element} currentTarget
   * @param {Element[]} allItems
   */
  #navigateWithinList(event, currentTarget, allItems) {
    if (allItems.length === 0) return;

    const currentIndex = allItems.indexOf(currentTarget);
    if (currentIndex === -1) return;

    let targetIndex;
    if (event.key === 'ArrowUp') {
      targetIndex = currentIndex > 0 ? currentIndex - 1 : allItems.length - 1;
    } else { // ArrowDown
      targetIndex = currentIndex < allItems.length - 1 ? currentIndex + 1 : 0;
    }

    const targetElement = allItems[targetIndex];
    if (targetElement && targetElement instanceof HTMLElement) {
      targetElement.focus();
    }
  }



  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement() {
    return this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    event?.preventDefault();
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   */
  open() {
    const details = this.#getDetailsElement();
    const summary = details.querySelector('summary');

    if (!summary) return;

    // Remember what to return focus to on close (e.g. the nav-bar toggle that opened us).
    this.#opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    details.setAttribute('open', '');
    requestAnimationFrame(() => details.classList.add('menu-open'));

    // Mirror the open state globally so toggles rendered OUTSIDE this component (the desktop nav-bar
    // "Shop" toggle, which opens this shared drawer by id and whose own <details> never opens) can
    // swap their open/close icon. The canonical drawer's own summary still uses `details[open]`.
    document.documentElement.setAttribute('data-menu-drawer-open', '');

    // Defer the outside-click listener one frame so the click that opened the drawer — which may
    // come from a toggle rendered OUTSIDE this component (the desktop nav-bar toggle opening the
    // shared canonical drawer by id) — doesn't immediately re-close it during its own bubble phase.
    // Track the rAF so a quick open-then-close (which runs #close before this frame) can cancel it
    // and avoid leaving the listener registered while the drawer is closed.
    this.#outsideClickRaf = requestAnimationFrame(() => {
      this.#outsideClickRaf = null;
      document.addEventListener('click', this.#handleDocumentClick);
    });
    trapFocus(details);

    // Move focus INTO the menu so keyboard users can immediately Tab/arrow through it. The drawer
    // can be opened by a toggle outside this component (the desktop nav-bar toggle), and the
    // canonical drawer's own <summary> is hidden at desktop widths — so trapFocus's
    // container.focus()/first-focusable lands on the (hidden) toggle, not the menu. Land on the
    // first level-1 item instead, which also makes the arrow-key navigation work.
    // Double rAF: the menu becomes visible via the `menu-open` class added above; focus must wait
    // until that style has been applied/painted, otherwise the (still computed-hidden) item is not
    // focusable yet and focus() is a no-op.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        const [firstItem] = this.#getLevel1NavigableItems();
        if (firstItem instanceof HTMLElement) firstItem.focus({ preventScroll: true });
      })
    );
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement());
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    details.classList.remove('menu-open');

    // Cancel a still-pending listener registration from open() (quick open-then-close), then remove
    // it if the frame already ran — together these guarantee no stray listener survives a close.
    if (this.#outsideClickRaf != null) {
      cancelAnimationFrame(this.#outsideClickRaf);
      this.#outsideClickRaf = null;
    }
    document.removeEventListener('click', this.#handleDocumentClick);

    onAnimationEnd(details, () => {
      details.removeAttribute('open');
      document.documentElement.removeAttribute('data-menu-drawer-open');

      removeTrapFocus();

      // Return focus to whatever opened the drawer (e.g. the nav-bar toggle), not <body>.
      if (this.#opener && this.#opener.isConnected) this.#opener.focus({ preventScroll: true });
      this.#opener = null;
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}
